import { useState } from 'react';
import { ConfirmWindow, WRITES_ENABLED } from '@/components/MaintenanceForm';
import { deleteCustomer } from './api';
import { Link, useNavigate } from 'react-router';
import { ApiError } from '@/api/client';
import { useSession } from '@/stores/session';
import { Tag, Dot } from '@/components/Tag';
import { Empty } from '@/components/States';
import { orderStatus } from '@/lib/status';
import { dateTime, money, shortDate } from '@/lib/format';
import { DetailError, DetailLoading, DetailShell, KV, Panel, detailStyles as d } from '@/features/products/kit/Detail';
import { useResource } from '@/features/products/kit/hooks';
import { activeState, invoiceType, settlementText } from './status';
import type { CustomerDetail as TCustomer } from './types';
import s from './PartnerDetail.module.css';

export function CustomerDetail({ id, embedded }: { id: string; embedded?: boolean }) {
  const nav = useNavigate();
  const [deleting, setDeleting] = useState(false);
  const can = useSession((st) => st.can);
  const path = `/api/customers/${id}`;
  const { data: c, loading, error, reload } = useResource<TCustomer>(path);

  if (error instanceof ApiError && error.status === 404) {
    return <Empty icon="info" title="客户不存在" action={<Link className={s.back} to="/customers">回客户列表</Link>}>可能已被删除、不在你负责的客户里，或者链接有误。</Empty>;
  }
  if (error) return <DetailError title="客户加载失败" error={error} path={path} onRetry={reload} />;
  if (loading || !c) return <DetailLoading />;

  const st = activeState(c.isActive);
  const stl = settlementText(c);
  // 老版客户详情：业务员列与金额列一起按 orders.amount.read 显示；金额另外以服务端是否返回为准
  const showSales = can('orders.amount.read');
  const canOrders = can('orders.read.all', 'orders.read.own', 'orders.read.assigned');

  const main = (
    <>
      <Panel title="联系信息">
        <KV
          rows={[
            ['联系人', c.contact || null],
            ['电话', c.phone ? <a className={`${s.tel} tnum`} href={`tel:${c.phone}`}>{c.phone}</a> : null],
            ['地址', c.address || null],
            ['归属业务员', c.ownerUser?.name || '未分配'],
            ['备注', c.note ? <span className={s.pre}>{c.note}</span> : null],
          ]}
        />
      </Panel>

      <Panel
        title="最近订单"
        note={c.orders.length >= 20 ? '只显示最近 20 张' : undefined}
        extra={canOrders ? <Link className={s.link} to={`/orders?q=${encodeURIComponent(c.name)}`}>全部订单</Link> : undefined}
        flush
      >
        {c.orders.length ? (
          <ul className={s.orders}>
            {c.orders.map((o) => {
              const os = orderStatus[o.status] || { label: o.status, tone: 'none' as const };
              return (
                <li key={o.id}>
                  <Link to={`/orders/${o.id}`}>
                    <div className={s.ot}>
                      <b className="tnum">{o.orderNo}</b>
                      <Tag tone={os.tone} sm>{os.label}</Tag>
                      {o.totalAmount !== undefined && <span className={`${s.amt} tnum`}>{money(o.totalAmount)}</span>}
                    </div>
                    <div className={s.om}>
                      <span className="tnum">{o.platformOrderNo || '未填 1688 单号'}</span>
                      {o.shopName && <span>{o.shopName}</span>}
                      <span>交货 <b className="tnum">{shortDate(o.deliveryDate)}</b></span>
                      {showSales && o.salesUser && <span>业务 {o.salesUser.name}</span>}
                      <span>跟单 {o.followerUser?.name || '未分配'}</span>
                      {o.warehouse && <span>{o.warehouse.name}</span>}
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        ) : <p className={d.none}>还没有你能看到的订单。</p>}
      </Panel>
    </>
  );

  const aside = (
    <>
      <Panel title="结算">
        <KV
          rows={[
            ['结算方式', <Dot tone={stl.dot}>{c.isMonthlySettlement ? '月结客户' : '非月结'}</Dot>],
            ['结算日', c.settlementDay ? `每月 ${c.settlementDay} 日` : null],
            ['账期', c.paymentTermDays != null ? `${c.paymentTermDays} 天` : null],
          ]}
        />
      </Panel>
      <Panel title="默认开票">
        <KV
          rows={[
            ['发票类型', c.defaultInvoiceType ? invoiceType[c.defaultInvoiceType] || c.defaultInvoiceType : null],
            ['开票抬头', c.defaultInvoiceTitle || c.name],
            ['税号', c.defaultInvoiceTaxNo ? <span className="tnum">{c.defaultInvoiceTaxNo}</span> : null],
            ['开票备注', c.defaultInvoiceNote || null],
          ]}
        />
      </Panel>
      <Panel title="记录">
        <KV rows={[['创建', <span className="tnum">{dateTime(c.createdAt)}</span>], ['更新', <span className="tnum">{dateTime(c.updatedAt)}</span>]]} />
      </Panel>
    </>
  );

  return (
    <DetailShell
      embedded={embedded}
      backTo="/customers"
      title={c.name}
      badges={st && <Tag tone={st.tone}>{st.label}</Tag>}
      openHref={`/customers/${c.id}`}
      actions={<>{can('customers.edit', 'taobao.manage') && <Link className={s.back} to={`/customers/${c.id}/edit`}>编辑</Link>}{can('customers.delete', 'taobao.manage') && <button type="button" className={s.back} disabled={!WRITES_ENABLED} onClick={() => setDeleting(true)}>删除</button>}</>}
      footer={deleting && <ConfirmWindow title={`删除客户「${c.name}」？`} confirm="确认删除" disabled={!WRITES_ENABLED} onClose={() => setDeleting(false)} onConfirm={async () => { await deleteCustomer(c.id); setDeleting(false); nav('/customers'); }}><p>已有业务订单或淘宝店订单的客户不能删除。可在编辑页面将其停用。</p></ConfirmWindow>}
      sub={<>归属 {c.ownerUser?.name || '未分配'} · {stl.label}</>}
      main={main}
      aside={aside}
    />
  );
}
